/* eslint-disable max-classes-per-file, react/no-multi-comp */
// Multiple classes and components used for HOC
/**
 * @typedef {import('../../shared/PropertyService/Comparables').default} Comps;
 * @typedef {import('./PropertyContext').default} Context
 * @typedef {import('./Props').default} Props
 * @typedef {import('./State').default} State
 * @typedef {import('./State').Property} Property
 */
import { AuthContext } from '../../../shared/AuthProvider';
import { getComparables } from '../homeowner-service';
import { selectedPropertyStorageKey } from '../../../config/local/properties';
import { toAbsoluteUrl } from '../../../utils/toAbsoluteUrl';
import { translate } from '../../Internationalization';
import { withPropertyService } from '../../../service-container';
import captureError from '../../../utils/captureError';
import images from '../../../config/local/images';
import LoadingScreen from '../../../shared/LoadingScreen';
import React from 'react';

/** @type {React.Context<Context>} */
export const PropertyContext = React.createContext(
	/** @type {Context} */ ({
		comparables: {
			listings: [],
			median: {
				listed: null,
				sold: null,
			},
		},
		propertyImage: null,
		propertyList: [],
		refreshPropertyList: () => Promise.resolve(),
		selectedProperty: null,
		setSelectedProperty: () => Promise.resolve(),
	})
);

/**
 * @param {Property[]} propertyList
 * @param {string | null | undefined} propertyId
 * @returns {Property | undefined}
 */
function resolveSelectedProperty(propertyList, propertyId) {
	if (propertyId) {
		const matchedProperty = propertyList.find(
			(property) => property.id === propertyId
		);

		if (matchedProperty) {
			return matchedProperty;
		}
	}

	return (
		propertyList.find((property) => property.isPrimary === true) ??
		propertyList.find(Boolean)
	);
}

/** @extends {React.Component<Props, State>} */
class PropertyProvider extends React.Component {
	#storageId = selectedPropertyStorageKey;
	/** @type {string | null} */
	#comparablesForPropertyId = null;
	/** @type {string | null} */
	#comparablesRequestForPropertyId = null;

	/** @type {import('../../../shared/AuthProvider/State').default | null} */
	context = null;

	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			comparables: {
				listings: [],
				median: {
					listed: null,
					sold: null,
				},
			},
			errorMessage: null,
			isGettingData: true,
			propertyImage: undefined,
			propertyList: [],
			refreshPropertyList: this.refreshPropertyList.bind(this),
			selectedProperty: null,
			setSelectedProperty: this.setSelectedProperty.bind(this),
		};
	}

	/**
	 * @param {unknown} error
	 * @returns {void}
	 */
	#error(error) {
		captureError(error);

		this.setState({ errorMessage: translate('global.error') });
	}

	/**
	 * @returns {Promise<void>}
	 */
	async componentDidMount() {
		const propertyList = await this.getPropertyList();
		const selectedProperty = this.getSelectedProperty(propertyList);
		const propertyImage = await this.getPropertyImage(selectedProperty);

		this.setState((state) => ({
			...state,
			isGettingData: false,
			propertyImage,
			propertyList,
			selectedProperty: selectedProperty ?? null,
		}));
	}

	/**
	 * @param {Props} prevProps
	 * @param {State} prevState
	 * @returns {Promise<void>}
	 */
	async componentDidUpdate(prevProps, prevState) {
		const propertyList = await this.getPropertyList();

		const selectedProperty = propertyList?.filter(
			(property) => property.id === this.state.selectedProperty?.id
		)[0];

		/*
		 * This checks if the selected property data returned by the API is the same as the selected property data currently in state.
		 * If they are not equal then update state with API data.
		 */
		if (
			JSON.stringify(prevState?.selectedProperty) !==
			JSON.stringify(selectedProperty)
		) {
			this.setState({
				isGettingData: false,
				propertyList,
				selectedProperty,
			});
		}

		if (propertyList?.length !== prevState.propertyList?.length) {
			this.setState((state) => ({
				...state,
				isGettingData: false,
				propertyList,
			}));
		}
		if (
			this.state.selectedProperty?.id &&
			prevState?.selectedProperty?.id !== this.state.selectedProperty?.id
		) {
			if (
				this.#comparablesForPropertyId ===
					this.state.selectedProperty.id ||
				this.#comparablesRequestForPropertyId ===
					this.state.selectedProperty.id
			) {
				return;
			}

			const accessToken = this.context?.accessToken;

			if (!accessToken) {
				this.setState({ isGettingData: false });
				return;
			}

			const propertyId = this.state.selectedProperty.id;
			this.#comparablesRequestForPropertyId = propertyId;

			try {
				const comparables = await getComparables(
					accessToken,
					propertyId
				);
				this.#comparablesForPropertyId = propertyId;

				this.setState((state) => ({
					...state,
					comparables,
				}));
			} finally {
				this.#comparablesRequestForPropertyId = null;
			}
		}
	}

	/**
	 * @returns {Promise<Property[]>}
	 */
	async getPropertyList() {
		try {
			/*
			 * TODO: This prop is often used to set the state. Using a prop to
			 * set state has unforeseen consequences.
			 *
			 * Ref: https://reactjs.org/docs/state-and-lifecycle.html#state-updates-may-be-asynchronous
			 */
			return this.props.propertyService.getHomeownerProperties();
		} catch (error) {
			/*
			 * TODO: This setting the state is can lead to unforeseen consequences
			 * as this is using props to set state and this method is called
			 * frequently to set the state.
			 */
			this.#error(error);
		}

		return [];
	}

	defaultImage = images.homeowners.properties.streetView;

	/**
	 * @param {Property | null | undefined} property
	 * @returns {Promise<string>}
	 */
	async getPropertyImage(property) {
		let propertyImage;

		if (!property?.id) {
			return toAbsoluteUrl(this.defaultImage);
		}

		try {
			propertyImage = await this.props.propertyService.getDefaultImage(
				property.id
			);
		} catch (error) {
			captureError(error);
		}

		return toAbsoluteUrl(propertyImage ?? this.defaultImage);
	}

	/**
	 * @param {Property[]} propertyList
	 * @returns {Property | undefined}
	 */
	getSelectedProperty(propertyList) {
		const storedId = localStorage.getItem(this.#storageId);

		return resolveSelectedProperty(propertyList, storedId);
	}

	/**
	 * @returns {Promise<void>}
	 */
	async refreshPropertyList() {
		const propertyList = await this.getPropertyList();
		const storedId = localStorage.getItem(this.#storageId);

		this.setState((prevState) => {
			const selected =
				resolveSelectedProperty(
					propertyList,
					prevState.selectedProperty?.id
				) ??
				resolveSelectedProperty(propertyList, storedId) ??
				null;

			if (selected?.id) {
				window.localStorage.setItem(this.#storageId, selected.id);
			}

			return {
				...prevState,
				isGettingData: false,
				propertyList,
				selectedProperty: selected,
			};
		});
	}

	/**
	 * @param {string} id
	 * @param {boolean} [flag]
	 * @returns {Promise<void>}
	 */
	async setSelectedProperty(id, flag = false) {
		const refreshed = flag ? await this.getPropertyList() : null;

		this.setState((prevState) => {
			const propertyList = refreshed ?? prevState.propertyList;

			const selected = propertyList.find(
				(property) => property.id === id
			);

			if (selected === undefined) {
				return { ...prevState };
			}

			if (prevState.selectedProperty?.id !== selected.id) {
				this.#comparablesForPropertyId = null;
				this.#comparablesRequestForPropertyId = null;
			}
			window.localStorage.setItem(this.#storageId, selected.id);
			return {
				...prevState,
				isGettingData: false,
				propertyList,
				selectedProperty: selected,
			};
		});
	}

	/**
	 * @returns {JSX.Element}
	 */
	render() {
		/** @type {React.ReactNode} */
		let content = this.props.children;

		if (this.state.isGettingData) {
			content = <LoadingScreen />;
		}

		return (
			<PropertyContext.Provider
				value={{
					comparables: this.state.comparables,
					propertyImage: this.state.propertyImage,
					propertyList: this.state.propertyList,
					refreshPropertyList: this.state.refreshPropertyList,
					selectedProperty: this.state.selectedProperty,
					setSelectedProperty: this.state.setSelectedProperty,
				}}
			>
				{content}
			</PropertyContext.Provider>
		);
	}
}

PropertyProvider.contextType = AuthContext;

// @ts-ignore TODO: Remove when service container types are fixed
export default withPropertyService(PropertyProvider);

/**
 * @template {object} TProps
 * @param {React.ComponentType<TProps & import('./PropertyContext').PropType>} Component
 * @returns {React.ComponentType<TProps>}
 */
export function withProperty(Component) {
	class PropertyComponent extends React.Component {
		render() {
			const property = /** @type {Context} */ (this.context);
			const props = /** @type {TProps} */ (this.props);

			/* eslint-disable-next-line react/jsx-props-no-spreading -- necessary for HOC */
			return <Component property={property} {...props} />;
		}
	}

	PropertyComponent.contextType = PropertyContext;

	return PropertyComponent;
}
